import { formatValue, formatValueBase } from "../utils/helpers";
import metricMetadataIconWhite from '../assets/metric-metadata-icon-white.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, Metric, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../utils/dashboardUtils";
import IconTooltip from "./IconTooltip";

interface DisaggregateMetricCardAltProps {
    id: number;
    metric: Metric;
    title: string;
    subText: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    showValue: boolean;
    color: string;
    svg?: string;
    showMetadataIcon?: boolean;
    altFormatting?: boolean;
};

const DisaggregateMetricCardAlt = ({ id, metric, title, subText, disaggData, geoDetails, geoTag, selectionActive, showValue, color, svg, showMetadataIcon = true, altFormatting = false }: DisaggregateMetricCardAltProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);

    // define metric id, value and unit
    const { value, unit } = metric?.versions?.[0] || {};

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }
        
        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);

        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 30,
                right: window.innerWidth - tooltipRect.right < 90,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }
    }, []);


    return (
        <div style={{ backgroundColor: color }} className="rounded-t-md">
            <div className="flex items-center justify-between px-2 h-8">
                <p className="text-white text-sm/4 font-bold">{title}</p>
                {showMetadataIcon && 
                    <div ref={tooltipRef} className="h-4 w-3 mb-1 group relative">
                        <button onClick={handleClick}>
                            <img src={metricMetadataIconWhite} alt="Metric metadata icon"/>
                        </button>
                        <IconTooltip edgeProximity={edgeProximity}/>
                    </div>
                }
            </div>
                
            <div className="flex items-center w-full justify-start px-2">
                <p className="text-white text-xs mr-2">{subText} </p>
                <h2 className="text-white text-md font-bold">
                    {showValue && value && unit && (
                        altFormatting
                        ? formatValueBase(value, unit)
                        : formatValue(value, unit)
                    )}
                </h2>
            </div>
            
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={metric.id}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default DisaggregateMetricCardAlt;
